import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi, categoriesApi, sellersApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import BulkImportExportModal from '../../components/BulkImportExportModal/BulkImportExportModal';
import './AdminProductManagePage.css';
import '../../components/AdminCreateModal.css';
import { useCurrency } from '../../context/CurrencyContext';
import { LOGO_PLACEHOLDER, PRODUCT_PLACEHOLDER } from '../../utils/placeholders';

// Inline SVG placeholder - no external URL dependency
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'%3E%3Crect width='56' height='56' fill='%23f0f0f0' rx='4'/%3E%3Cpath d='M14 42l12-16 8 11 6-8 10 13H14z' fill='%23ccc'/%3E%3Ccircle cx='19' cy='22' r='4' fill='%23ccc'/%3E%3C/svg%3E";

// Helper function to get product image from image field or images array
const INVALID_IMAGE_PATTERNS = [
  '/default-product.png',
  'da821e6a6c344d588c41324f62b0dda825ecff92',
  'via.placeholder.com',
  'pexels.com/photo/',
];
const isValidImageUrl = (url) =>
  url && !INVALID_IMAGE_PATTERNS.some((p) => url.includes(p));

const getProductImage = (product) => {
  // 1. Check productImages relation (separate table)
  if (product.productImages && product.productImages.length > 0) {
    const url = product.productImages[0].url;
    if (isValidImageUrl(url)) return url;
  }

  // 2. Check images JSON array
  if (product.images) {
    try {
      const imagesArray = typeof product.images === 'string'
        ? JSON.parse(product.images)
        : product.images;
      if (Array.isArray(imagesArray) && imagesArray.length > 0 && isValidImageUrl(imagesArray[0])) {
        return imagesArray[0];
      }
    } catch (e) {
      console.error('Error parsing product images:', e);
    }
  }

  // 3. Check main image field
  if (isValidImageUrl(product.image)) {
    return product.image;
  }

  return null;
};

const AdminProductManagePage = () => {
  const navigate = useNavigate();
  const { formatPriceRange: currFormatPriceRange } = useCurrency();
  
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Action dropdown state
  const [actionDropdown, setActionDropdown] = useState({ show: false, productId: null });
  
  // Product Detail Modal state - kept for possible future use
  // eslint-disable-next-line no-unused-vars
  const [showDetailModal, setShowDetailModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [createForm, setCreateForm] = useState({
    name: '', description: '', priceMin: '', priceMax: '', unit: 'piece',
    moq: '', categoryId: '', sellerId: '', status: 'published'
  });

  const resetCreateForm = () => {
    setCreateForm({ name: '', description: '', priceMin: '', priceMax: '', unit: 'piece', moq: '', categoryId: '', sellerId: '', status: 'published' });
    setCreateErrors({});
  };

  const handleCreateProduct = async () => {
    const errors = {};
    if (!createForm.name.trim()) errors.name = 'Product name is required';
    if (!createForm.categoryId) errors.categoryId = 'Category is required';
    if (Object.keys(errors).length > 0) { setCreateErrors(errors); return; }
    try {
      setCreateSaving(true);
      await productsApi.adminCreate(createForm);
      setShowCreateModal(false);
      resetCreateForm();
      fetchProducts();
    } catch (err) {
      setCreateErrors({ submit: err.response?.data?.error || 'Failed to create product' });
    } finally {
      setCreateSaving(false);
    }
  };

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // showAll=true to see all products including hidden ones (admin view)
      const response = await productsApi.getAll({ limit: 200, showAll: true });
      const productData = Array.isArray(response) ? response : (response.products || []);
      setProducts(productData);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Fetch sellers from API
  const fetchSellers = useCallback(async () => {
    try {
      const response = await sellersApi.getAll({ limit: 500 });
      const sellerData = Array.isArray(response) ? response : (response.sellers || []);
      setSellers(sellerData);
    } catch (err) {
      console.error('Error fetching sellers:', err);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSellers();
  }, [fetchProducts, fetchCategories, fetchSellers]);

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.seller?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || product.categoryId === parseInt(selectedCategory);
    const matchesStatus = !selectedStatus || product.status?.toLowerCase() === selectedStatus.toLowerCase();
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedStatus]);

  // Handle select item
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedProducts.map(p => p.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle status change using dedicated lifecycle endpoints
  const handleStatusChange = async (productId, newStatus) => {
    try {
      switch (newStatus) {
        case 'approved':
          await productsApi.approve(productId);
          break;
        case 'published': {
          const product = products.find(p => p.id === productId);
          if (product && !['approved', 'unpublished'].includes(product.status)) {
            await productsApi.approve(productId);
          }
          await productsApi.publish(productId);
          break;
        }
        case 'unpublished':
          await productsApi.unpublish(productId);
          break;
        case 'rejected':
          await productsApi.reject(productId);
          break;
        default:
          await productsApi.updateStatus(productId, newStatus);
      }
      fetchProducts();
      setActionDropdown({ show: false, productId: null });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsApi.delete(productId);
        fetchProducts(); // Refresh list
        setActionDropdown({ show: false, productId: null });
      } catch (err) {
        console.error('Error deleting product:', err);
        alert('Failed to delete product');
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select products to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} products?`)) {
      try {
        await productsApi.bulkDelete(selectedItems);
        setSelectedItems([]);
        fetchProducts();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete products');
      }
    }
  };

  // Handle Export CSV
  // eslint-disable-next-line no-unused-vars
  const handleExportCSV = () => {
    const dataToExport = filteredProducts;
    if (dataToExport.length === 0) {
      alert('No products to export');
      return;
    }

    // Create CSV headers
    const headers = ['ID', 'Product Name', 'Company', 'Category', 'Price Min', 'Price Max', 'Unit', 'Status', 'Created Date'];
    
    // Create CSV rows
    const rows = dataToExport.map(product => [
      product.id,
      `"${(product.name || '').replace(/"/g, '""')}"`,
      `"${(product.seller?.name || 'N/A').replace(/"/g, '""')}"`,
      `"${(product.category?.name || 'N/A').replace(/"/g, '""')}"`,
      product.priceMin || '',
      product.priceMax || '',
      product.unit || '',
      product.status || 'pending',
      product.createdAt ? new Date(product.createdAt).toLocaleDateString() : ''
    ]);

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `products-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (status) => {
    if (selectedItems.length === 0) {
      alert('Please select products to update');
      return;
    }
    try {
      await productsApi.bulkUpdateStatus(selectedItems, status);
      setSelectedItems([]);
      fetchProducts();
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update products');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Format price
  const formatPrice = (product) => {
    if (!product.priceMin) return 'N/A';
    return `${currFormatPriceRange(product.priceMin, product.priceMax || product.priceMin)} / ${product.unit || 'Unit'}`;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.apm-dropdown')) {
        setShowCategoryDropdown(false);
        setShowStatusDropdown(false);
        setShowFilterDropdown(false);
      }
      if (!e.target.closest('.apm-action-dropdown')) {
        setActionDropdown({ show: false, productId: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Open product detail modal - kept for possible future use
  // eslint-disable-next-line no-unused-vars
  const openProductDetail = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  // Get supplier verification level (company-level)
  const getVerificationLevel = (product) => {
    const seller = product.seller;
    if (!seller) return { label: 'No Supplier', level: 'none' };
    if (seller.verificationLevel === 'certified') return { label: 'Certified', level: 'certified' };
    if (seller.verificationLevel === 'inspected') return { label: 'Inspected', level: 'inspected' };
    if (seller.isVerified || seller.verificationLevel === 'verified') return { label: 'Verified', level: 'verified' };
    return { label: 'Not Verified', level: 'not-verified' };
  };

  // Get membership plan
  const getMembershipPlan = (product) => {
    if (product.seller?.membershipPlan) {
      // Return only first word (Global or GCC)
      return product.seller.membershipPlan.split(' ')[0];
    }
    if (product.isFeatured) return 'Premium';
    return 'Basic';
  };

  return (
    <AdminLayout>
      <div className="admin-product-manage-content">
        <div className="apm-page-header">
          <div className="apm-page-title-section">
            <h1 className="apm-page-title">Product Manage</h1>
            <p className="apm-page-description">Oversee, approve, and organize all product listings across the platform</p>
          </div>
          <div className="apm-header-actions">
            <button className="apm-bulk-io-btn" onClick={() => setShowBulkModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Import / Export
            </button>
            <button className="admin-add-new-btn" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
              Add Product
            </button>
            {selectedItems.length > 0 && (
              <>
                <button className="apm-bulk-btn apm-bulk-active" onClick={() => handleBulkStatusUpdate('approved')}>
                  Approve ({selectedItems.length})
                </button>
                <button className="apm-bulk-btn apm-bulk-publish" onClick={() => handleBulkStatusUpdate('published')}>
                  Publish ({selectedItems.length})
                </button>
                <button className="apm-bulk-btn apm-bulk-hide" onClick={() => handleBulkStatusUpdate('unpublished')}>
                  Unpublish ({selectedItems.length})
                </button>
                <button className="apm-bulk-btn apm-bulk-archive" onClick={() => handleBulkStatusUpdate('archived')}>
                  Archive ({selectedItems.length})
                </button>
                <button className="apm-bulk-btn apm-bulk-delete" onClick={handleBulkDelete}>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>

        {/* Product Lists Header - Outside white content area */}
        <div className="apm-list-header">
          <h2 className="apm-list-title">Product Lists</h2>
          <div className="apm-list-controls">
            <div className="apm-search-box">
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            
            {/* Country Filter - Native Select */}
            <div className="apm-select-wrapper">
              <select 
                className="apm-filter-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Country</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id.toString()}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            {/* Status Filter - Native Select */}
            <div className="apm-select-wrapper">
              <select 
                className="apm-filter-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
                <option value="archived">Archived</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            {/* Filter By Category - Native Select */}
            <div className="apm-select-wrapper apm-category-select">
              <svg className="apm-filter-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
              </svg>
              <select 
                className="apm-filter-select with-icon"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Filter By Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id.toString()}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* White Content Area - Only contains table */}
        <div className="apm-content">
          {loading ? (
            <div className="apm-loading">Loading products...</div>
          ) : error ? (
            <div className="apm-error">{error} <button onClick={fetchProducts}>Retry</button></div>
          ) : (
          <div className="apm-table">
            <div className="apm-table-header">
              <div className="apm-th apm-th-checkbox">
                <input 
                  type="checkbox" 
                  checked={paginatedProducts.length > 0 && selectedItems.length === paginatedProducts.length}
                  onChange={handleSelectAll}
                />
                <span>All</span>
              </div>
              <div className="apm-th">Image</div>
              <div className="apm-th">Creation date</div>
              <div className="apm-th">Product Title</div>
              <div className="apm-th">Company Name</div>
              <div className="apm-th">Supplier Verification</div>
              <div className="apm-th">Price</div>
              <div className="apm-th">Membership Plan</div>
              <div className="apm-th">Product Status</div>
              <div className="apm-th">Details</div>
              <div className="apm-th">Action</div>
            </div>
            <div className="apm-table-body">
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map((product) => (
                  <div key={product.id} className="apm-table-row">
                    <div className="apm-td apm-td-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedItems.includes(product.id)}
                        onChange={() => handleSelectItem(product.id)}
                      />
                    </div>
                    <div className="apm-td apm-td-image">
                      <img 
                        src={getProductImage(product) || PLACEHOLDER_IMAGE} 
                        alt={product.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE; }}
                      />
                    </div>
                    <div className="apm-td apm-td-date" data-label="Date">{formatDate(product.createdAt)}</div>
                    <div className="apm-td apm-td-name" title={product.name || product.description}>{product.name || product.description}</div>
                    <div className="apm-td apm-td-company" data-label="Company">{product.seller?.name || 'N/A'}</div>
                    <div className="apm-td apm-td-verification" data-label="Verification">
                      {(() => {
                        const v = getVerificationLevel(product);
                        return <span className={`apm-verification-badge ${v.level}`}>{v.label}</span>;
                      })()}
                    </div>
                    <div className="apm-td apm-td-price" data-label="Price">{formatPrice(product)}</div>
                    <div className="apm-td apm-td-membership" data-label="Plan">{getMembershipPlan(product)}</div>
                    <div className="apm-td apm-td-status" data-label="Status">
                      <span className={`apm-status-badge ${(product.status || 'pending_approval').toLowerCase().replace('_', '-')}`}>
                        {{
                          'draft': 'Draft',
                          'pending_approval': 'Pending Approval',
                          'approved': 'Approved',
                          'published': 'Published',
                          'unpublished': 'Unpublished',
                          'archived': 'Archived',
                          'rejected': 'Rejected'
                        }[product.status] || product.status || 'Pending'}
                      </span>
                    </div>
                    <div className="apm-td apm-td-details">
                      <button className="apm-details-btn" onClick={() => navigate(`/admin/products/${product.id}`)}>Details</button>
                    </div>
                    <div className="apm-td apm-td-action">
                      <div className="apm-action-dropdown">
                        <button 
                          className="apm-more-btn"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setActionDropdown({ 
                              show: actionDropdown.productId === product.id ? !actionDropdown.show : true, 
                              productId: product.id 
                            }); 
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="5" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="19" r="2" fill="#6D6D6D"/>
                          </svg>
                        </button>
                        {actionDropdown.show && actionDropdown.productId === product.id && (
                          <div className="apm-action-menu">
                            <div className="apm-action-item" onClick={() => navigate(`/product-input?edit=${product.id}&admin=true`)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Edit
                            </div>
                            {['draft', 'pending_approval', 'rejected'].includes(product.status) && (
                              <div className="apm-action-item" onClick={() => handleStatusChange(product.id, 'approved')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Approve
                              </div>
                            )}
                            {['draft', 'pending_approval', 'rejected', 'approved', 'unpublished'].includes(product.status) && (
                              <div className="apm-action-item" onClick={() => handleStatusChange(product.id, 'published')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                {['draft', 'pending_approval', 'rejected'].includes(product.status) ? 'Approve & Publish' : 'Publish'}
                              </div>
                            )}
                            {product.status === 'published' && (
                              <div className="apm-action-item" onClick={() => handleStatusChange(product.id, 'unpublished')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Unpublish
                              </div>
                            )}
                            {['pending_approval', 'draft'].includes(product.status) && (
                              <div className="apm-action-item" style={{color: '#ef4444'}} onClick={() => handleStatusChange(product.id, 'rejected')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/></svg>
                                Reject
                              </div>
                            )}
                            {!['archived'].includes(product.status) && (
                              <div className="apm-action-item" onClick={() => handleStatusChange(product.id, 'archived')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="21 8 21 21 3 21 3 8" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><rect x="1" y="3" width="22" height="5" rx="1" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="10" y1="12" x2="14" y2="12" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Archive
                              </div>
                            )}
                            <div className="apm-action-item apm-action-delete" onClick={() => handleDeleteProduct(product.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="apm-no-data">No products found</div>
              )}
            </div>
          </div>
          )}

          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Product Detail Modal */}
      {showDetailModal && selectedProduct && (
        <div className="apm-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="apm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="apm-modal-header">
              <div className="apm-modal-company-header">
                <img 
                  src={selectedProduct.seller?.logo || LOGO_PLACEHOLDER} 
                  alt={selectedProduct.seller?.name}
                  className="apm-modal-company-logo"
                />
                <div className="apm-modal-company-info">
                  <h2>{selectedProduct.seller?.name || 'Company Name'}</h2>
                  <div className="apm-modal-badges">
                    {selectedProduct.seller?.isVerified && (
                      <span className="apm-badge verified">Verified</span>
                    )}
                    <span className="apm-badge">{selectedProduct.seller?.country || 'UAE'}</span>
                  </div>
                  <p className="apm-modal-stats">
                    <span>0 Reviews</span> | <span>{products.filter(p => p.sellerId === selectedProduct.sellerId).length} Products</span>
                  </p>
                </div>
              </div>
              <button className="apm-modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>

            <div className="apm-modal-body">
              {/* Cover Image */}
              <div className="apm-modal-cover">
                <img 
                  src={selectedProduct.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'} 
                  alt={selectedProduct.name}
                />
              </div>

              {/* Company Info Section */}
              <div className="apm-modal-section">
                <h3>{selectedProduct.seller?.name || 'Company Name'}</h3>
                <p className="apm-modal-tagline">A Leading Construction Supplier with {new Date().getFullYear() - 2010}+ Years of Industry Experience</p>
                <p className="apm-modal-description">{selectedProduct.description || 'No description available.'}</p>
                
                <div className="apm-modal-links">
                  <button className="apm-modal-link" onClick={() => navigate(`/seller/${selectedProduct.seller?.slug || selectedProduct.sellerId}`)}>Company Link</button>
                </div>

                <div className="apm-modal-certifications">
                  <h4>Certifications & Accreditations</h4>
                  <p>An open statement for a trading marketplace and detailed web design studio content Sed vitae...</p>
                </div>
              </div>

              {/* Basic Information */}
              <div className="apm-modal-section">
                <h4>Basic Information</h4>
                <div className="apm-modal-info-grid">
                  <div className="apm-modal-info-item">
                    <label>Business Type</label>
                    <span>Manufacturer</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>License Number</label>
                    <span>{selectedProduct.seller?.licenseNumber || 'N/A'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Sector of Expertise</label>
                    <span>{selectedProduct.category?.name || 'N/A'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Company</label>
                    <span>{selectedProduct.seller?.name || 'N/A'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Core Offerings</label>
                    <span>{selectedProduct.name || 'N/A'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Year of Establishment</label>
                    <span>{selectedProduct.seller?.establishedYear || '2010'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Total Employee</label>
                    <span>{selectedProduct.seller?.employeeCount || '50-100'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Location Services</label>
                    <span>{selectedProduct.seller?.serviceLocations || 'UAE, Saudi Arabia, Qatar'}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="apm-modal-section">
                <h4>Contact Information</h4>
                <div className="apm-modal-info-grid">
                  <div className="apm-modal-info-item">
                    <label>Telephone</label>
                    <span>{selectedProduct.seller?.phone || '+971 XX XXX XXXX'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Address</label>
                    <span>{selectedProduct.seller?.address || 'Dubai, UAE'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>PO Box</label>
                    <span>{selectedProduct.seller?.poBox || 'N/A'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Country</label>
                    <span>{selectedProduct.seller?.country || 'UAE'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>City</label>
                    <span>{selectedProduct.seller?.city || 'Dubai'}</span>
                  </div>
                  <div className="apm-modal-info-item">
                    <label>Fax</label>
                    <span>{selectedProduct.seller?.fax || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Company Overview */}
              <div className="apm-modal-section">
                <h4>Company Overview</h4>
                <p className="apm-modal-overview">{selectedProduct.seller?.overview || selectedProduct.description || 'No company overview available.'}</p>
              </div>

              {/* Company Notes */}
              <div className="apm-modal-section">
                <h4>Company Notes</h4>
                <p className="apm-modal-notes">{selectedProduct.seller?.notes || 'No additional notes.'}</p>
              </div>

              {/* Company Snapshot */}
              <div className="apm-modal-section">
                <h4>Company Snapshot</h4>
                <div className="apm-modal-snapshots">
                  <img src={getProductImage(selectedProduct) || PRODUCT_PLACEHOLDER} alt="Snapshot 1" />
                  <img src={getProductImage(selectedProduct) || PRODUCT_PLACEHOLDER} alt="Snapshot 2" />
                  <img src={getProductImage(selectedProduct) || PRODUCT_PLACEHOLDER} alt="Snapshot 3" />
                  <img src={getProductImage(selectedProduct) || PRODUCT_PLACEHOLDER} alt="Snapshot 4" />
                </div>
              </div>

              {/* Brochure */}
              <div className="apm-modal-section">
                <h4>Brochure</h4>
                <button className="apm-download-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Download
                </button>
              </div>
            </div>

            <div className="apm-modal-footer">
              <button className="apm-cancel-btn" onClick={() => setShowDetailModal(false)}>Cancel</button>
              <button className="apm-approve-btn" onClick={() => { handleStatusChange(selectedProduct.id, 'approved'); setShowDetailModal(false); }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showCreateModal && (
        <div className="admin-create-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-create-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-create-modal-header">
              <h3>Add New Product</h3>
              <button className="admin-create-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="admin-create-modal-body">
              <div className="admin-create-form-group">
                <label>Product Name <span className="required">*</span></label>
                <input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="Enter product name" />
                {createErrors.name && <div className="admin-create-form-error">{createErrors.name}</div>}
              </div>
              <div className="admin-create-form-group">
                <label>Category <span className="required">*</span></label>
                <select value={createForm.categoryId} onChange={e => setCreateForm({...createForm, categoryId: e.target.value})}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {createErrors.categoryId && <div className="admin-create-form-error">{createErrors.categoryId}</div>}
              </div>
              <div className="admin-create-form-group">
                <label>Supplier</label>
                <select value={createForm.sellerId} onChange={e => setCreateForm({...createForm, sellerId: e.target.value})}>
                  <option value="">Auto-assign</option>
                  {sellers.map(s => <option key={s.id} value={s.id}>{s.name}{s.country ? ` (${s.country})` : ''}</option>)}
                </select>
              </div>
              <div className="admin-create-form-group">
                <label>Description</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder="Product description" />
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Min Price</label>
                  <input type="number" value={createForm.priceMin} onChange={e => setCreateForm({...createForm, priceMin: e.target.value})} placeholder="0.00" />
                </div>
                <div className="admin-create-form-group">
                  <label>Max Price</label>
                  <input type="number" value={createForm.priceMax} onChange={e => setCreateForm({...createForm, priceMax: e.target.value})} placeholder="0.00" />
                </div>
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Unit</label>
                  <select value={createForm.unit} onChange={e => setCreateForm({...createForm, unit: e.target.value})}>
                    <option value="piece">Piece</option>
                    <option value="kg">Kg</option>
                    <option value="ton">Ton</option>
                    <option value="meter">Meter</option>
                    <option value="sqm">Sqm</option>
                    <option value="set">Set</option>
                    <option value="box">Box</option>
                  </select>
                </div>
                <div className="admin-create-form-group">
                  <label>MOQ</label>
                  <input type="number" value={createForm.moq} onChange={e => setCreateForm({...createForm, moq: e.target.value})} placeholder="Minimum order qty" />
                </div>
              </div>
              <div className="admin-create-form-group">
                <label>Status</label>
                <select value={createForm.status} onChange={e => setCreateForm({...createForm, status: e.target.value})}>
                  <option value="published">Published</option>
                  <option value="unpublished">Unpublished</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              {createErrors.submit && <div className="admin-create-form-error">{createErrors.submit}</div>}
            </div>
            <div className="admin-create-modal-footer">
              <button className="admin-create-cancel-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="admin-create-save-btn" onClick={handleCreateProduct} disabled={createSaving}>{createSaving ? 'Creating...' : 'Create Product'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import/Export Modal */}
      <BulkImportExportModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        entityType="products"
        isAdmin={true}
        onImportSuccess={fetchProducts}
      />
    </AdminLayout>
  );
};

export default AdminProductManagePage;
